import { toastService } from "cruzo";
import { editorOpenAiChatCompletionsStream } from "site/services/editor-openai.service";
import { readEditorProofreadPromptOverride } from "site/services/editor-proofread-prompt.service";
import {
  buildProofreadLessonZeptoPayload,
  buildProofreadRowZeptoPayload,
  buildProofreadTranslationHints,
  collectAllProofreadItems,
  collectProofreadItemsForRow,
  isValidZeptoType,
  proofreadItemId,
  zeptoProofreadFieldSyntaxValid,
  zeptoRowProofreadSyntaxValid,
  type ProofreadItem,
  type ProofreadLessonZeptoPayload,
  type ZeptoRowEdit,
  zeptoToRows,
} from "site/services/zepto-editor";

const PROOFREAD_MODEL = "gpt-5.4-nano";
const PROOFREAD_STRUCTURE_MAX_ATTEMPTS = 3;
export const PROOFREAD_ROWS_BATCH_SIZE = 20;

function chunkProofreadRows(rows: ZeptoRowEdit[], batchSize: number): ZeptoRowEdit[][] {
  if (batchSize <= 0) {
    return rows.length ? [rows] : [];
  }
  const out: ZeptoRowEdit[][] = [];
  for (let i = 0; i < rows.length; i += batchSize) {
    out.push(rows.slice(i, i + batchSize));
  }
  return out;
}

export function proofreadLessonBatchCount(rows: ZeptoRowEdit[]): number {
  const batches = chunkProofreadRows(rows, PROOFREAD_ROWS_BATCH_SIZE);
  return batches.length > 0 ? batches.length : 1;
}

export function proofreadLessonBatchRows(
  rows: ZeptoRowEdit[],
  batchIndex: number,
): ZeptoRowEdit[] {
  const batches = chunkProofreadRows(rows, PROOFREAD_ROWS_BATCH_SIZE);
  if (!batches.length) {
    return [];
  }
  return batches[batchIndex] ?? [];
}

function translationHintsForBatch(
  allRows: ZeptoRowEdit[],
  titleArabic: string,
  titleRussian: string,
  batchRows: ZeptoRowEdit[],
  includeTitles: boolean,
) {
  const batchIds = new Set(batchRows.map((row) => row.rowId));
  return buildProofreadTranslationHints(allRows, titleArabic, titleRussian).filter((hint) => {
    if (hint.row_id == null) {
      return includeTitles;
    }
    return batchIds.has(hint.row_id);
  });
}

function notifyProofreadStructureRetry(attempt: number, maxAttempts: number): void {
  toastService.show({
    kind: "error",
    message: `API неудачно обработал ответ. Запускаем проверку заново (${attempt + 1} из ${maxAttempts}).`,
    timeoutMs: 5000,
    alignX: "center",
    alignY: "top",
  });
}

function isProofreadStructureError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return (
    error.message.startsWith("OpenAI proofread: изменил") ||
    error.message.startsWith("OpenAI proofread: изменился") ||
    error.message.startsWith("OpenAI proofread: недопустим") ||
    error.message.startsWith("OpenAI proofread: неизвестный тип") ||
    error.message === "OpenAI proofread: ожидалась одна строка zepto"
  );
}

export function proofreadTextUnchanged(current: string, suggested: string): boolean {
  if (current === suggested) {
    return true;
  }
  const suggestedTrim = suggested.trim();
  if (!suggestedTrim) {
    return true;
  }
  const currentTrim = current.trim();
  if (currentTrim === suggestedTrim) {
    return true;
  }
  const norm = (value: string) => value.normalize("NFC").replace(/\r\n/g, "\n");
  return norm(currentTrim) === norm(suggestedTrim);
}

export type ProofreadSuggestionResult = {
  id: string;
  suggested: string;
  note?: string;
};

export type ProofreadProgress = {
  batchIndex: number;
  batchCount: number;
  itemsDone: number;
  itemsTotal: number;
  rowsDone: number;
  rowsTotal: number;
  percent: number;
  streaming: boolean;
};

export function proofreadStreamPercent(receivedChars: number, expectedChars: number): number {
  if (receivedChars <= 0) {
    return 0;
  }
  if (expectedChars <= 0) {
    return 50;
  }
  const raw = Math.floor((receivedChars / expectedChars) * 100);
  return Math.max(1, Math.min(95, raw));
}

export type ProofreadLessonSource = {
  rows: ZeptoRowEdit[];
  titleArabic: string;
  titleRussian: string;
};

export const DEFAULT_PROOFREAD_SYSTEM_PROMPT = [
  "You proofread Arabic-lesson JSON for a language course. You have exactly two jobs:",
  "",
  "1. ARABIC — correct spelling and full vocalization (harakat / tashkīl) on every pronounced word.",
  "2. RUSSIAN — correct the final learner-facing translation after decoding exercise markup (use translation_hints).",
  "",
  "INPUT: title_arabic, title_russian, zepto (array of [type, content, id]), translation_hints (read-only).",
  "OUTPUT: title_arabic, title_russian, zepto only. Omit translation_hints.",
  "",
  "STRUCTURE (never violate): same zepto row count, ids, order, and type per row. Never add, remove, reorder, or retype rows. Type 7 (rule) rows: copy unchanged.",
  "",
  "── 1. ARABIC ──",
  "Editable fields: title_arabic; zepto content[0] for types 1/2/4/5/6; type 3 task sentence (#...# blanks) and task answers (#-separated variants).",
  "Language: Modern Standard Arabic (fusha).",
  "",
  "Preserve lesson markup exactly — edit only letters and diacritics:",
  "  • Types 1, 2, 4 — plain Arabic, no | # * build/select markup.",
  "  • Type 3 — #...# blanks in the sentence; answer variants separated by #.",
  "  • Type 5 (build) — |*part*part*||optional_suffix|.",
  "  • Type 6 (select) — |+correct-distractor-distractor| (+ marks correct, - separates options).",
  "",
  "Harakat: vocalize every word the learner should read aloud. Fix wrong/missing harakat, shadda, tanwīn, sukūn, hamza seat, alif/ya/waw seat, and letter errors. Remove stray or duplicate diacritics. Keep correct existing tashkīl. No stylistic rewrites when orthography is already correct.",
  "",
  "── 2. RUSSIAN ──",
  "Editable fields: title_russian; zepto content[1] for all types including 3.",
  "Russian is plain text for learners — NEVER copy exercise markup: no |, +, -, #, *, option lists, or hyphen-separated distractors.",
  "",
  "For each Russian field, read the matching translation_hints entry (same row_id and field):",
  "  • arabic_for_translation, built_arabic, arabic_prompt, correct_arabic — decoded Arabic meaning.",
  "  • translation_rules, russian_structure — expected shape of the final Russian text.",
  "",
  "Write the FINAL Russian a student should see:",
  "  • Types 1, 2, 4 — natural translation of arabic_for_translation.",
  "  • Type 3 — translation of built_arabic (sentence with blanks already filled).",
  "  • Type 5 — one natural sentence matching built_arabic.",
  "  • Type 6 — translate arabic_prompt as the exercise prompt; add the correct answer in Russian (words from correct_arabic, translated).",
  "",
  "Fix Russian only for clear errors: empty field while Arabic exists, Arabic script in Russian, markup leaked into Russian, or meaning mismatch with decoded Arabic.",
  "Do not swap synonyms or polish style when the meaning is already correct. When unsure — leave unchanged.",
].join("\n");

function systemPromptAll(): string {
  const custom = readEditorProofreadPromptOverride();
  if (custom) {
    return custom;
  }
  return DEFAULT_PROOFREAD_SYSTEM_PROMPT;
}

function collectProofreadItemsForSource(
  source: ProofreadLessonSource,
  rowId?: number,
): ProofreadItem[] {
  const { rows, titleArabic, titleRussian } = source;
  if (rowId != null) {
    return collectProofreadItemsForRow(rowId, rows, titleArabic, titleRussian);
  }
  return collectAllProofreadItems(rows, titleArabic, titleRussian);
}

function parseProofreadLessonResponse(content: string): ProofreadLessonZeptoPayload {
  let parsed: unknown;
  try {
    parsed = JSON.parse(content);
  } catch {
    throw new Error("OpenAI proofread: invalid JSON response");
  }
  if (!parsed || typeof parsed !== "object") {
    throw new Error("OpenAI proofread: invalid JSON response");
  }
  const row = parsed as Record<string, unknown>;
  if (!Array.isArray(row.zepto)) {
    throw new Error("OpenAI proofread: в ответе нет zepto");
  }
  return {
    title_arabic: String(row.title_arabic ?? ""),
    title_russian: String(row.title_russian ?? ""),
    zepto: row.zepto,
  };
}

function assertProofreadTypesKnown(correctedRows: ZeptoRowEdit[]): void {
  for (const row of correctedRows) {
    if (!isValidZeptoType(row.type)) {
      throw new Error("OpenAI proofread: неизвестный тип строки zepto");
    }
  }
}

function assertProofreadSyntaxValid(correctedRows: ZeptoRowEdit[]): void {
  for (const row of correctedRows) {
    if (!zeptoRowProofreadSyntaxValid(row)) {
      throw new Error("OpenAI proofread: недопустимая разметка zepto");
    }
  }
}

function effectiveProofreadRowType(
  item: ProofreadItem,
  correctedRows: ZeptoRowEdit[],
  correctedById: Map<string, string>,
): number | null {
  if (item.rowId == null) {
    return item.rowType ?? null;
  }
  const typeSuggestion = correctedById.get(proofreadItemId(item.rowId, "type"));
  if (typeSuggestion != null) {
    const nextType = Number(typeSuggestion);
    if (isValidZeptoType(nextType)) {
      return nextType;
    }
  }
  const correctedRow = correctedRows.find((row) => row.rowId === item.rowId);
  return correctedRow?.type ?? item.rowType ?? null;
}

function isAllowedProofreadSuggestion(
  item: ProofreadItem,
  suggested: string,
  correctedRows: ZeptoRowEdit[],
  correctedById: Map<string, string>,
): boolean {
  if (item.field === "type") {
    return false;
  }
  const rowType = effectiveProofreadRowType(item, correctedRows, correctedById);
  if (rowType == null) {
    return zeptoProofreadFieldSyntaxValid(1, item.field, suggested);
  }
  return zeptoProofreadFieldSyntaxValid(rowType, item.field, suggested);
}

function assertProofreadTypesUnchanged(
  sourceRows: ZeptoRowEdit[],
  correctedRows: ZeptoRowEdit[],
): void {
  for (let i = 0; i < sourceRows.length; i += 1) {
    if (sourceRows[i].type !== correctedRows[i].type) {
      throw new Error("OpenAI proofread: изменился тип строки zepto");
    }
  }
}

function assertProofreadStructureUnchanged(
  sourceRows: ZeptoRowEdit[],
  correctedRows: ZeptoRowEdit[],
): void {
  if (correctedRows.length !== sourceRows.length) {
    throw new Error("OpenAI proofread: изменилось число строк zepto");
  }
  for (let i = 0; i < sourceRows.length; i += 1) {
    if (sourceRows[i].rowId !== correctedRows[i].rowId) {
      throw new Error("OpenAI proofread: изменились id строк zepto");
    }
  }
}

function parseProofreadCorrectedRows(
  content: string,
  sourceRows: ZeptoRowEdit[],
  rowId?: number,
): {
  correctedRows: ZeptoRowEdit[];
  correctedTitleArabic: string;
  correctedTitleRussian: string;
} {
  const corrected = parseProofreadLessonResponse(content);
  let correctedRows: ZeptoRowEdit[];
  try {
    correctedRows = zeptoToRows(corrected.zepto).rows;
  } catch {
    throw new Error("OpenAI proofread: не удалось разобрать zepto в ответе");
  }
  assertProofreadStructureUnchanged(sourceRows, correctedRows);
  assertProofreadTypesUnchanged(sourceRows, correctedRows);
  assertProofreadTypesKnown(correctedRows);
  assertProofreadSyntaxValid(correctedRows);
  if (rowId != null) {
    if (correctedRows.length !== 1) {
      throw new Error("OpenAI proofread: ожидалась одна строка zepto");
    }
  }
  return {
    correctedRows,
    correctedTitleArabic: corrected.title_arabic,
    correctedTitleRussian: corrected.title_russian,
  };
}

export function diffProofreadItems(
  originalItems: ProofreadItem[],
  correctedRows: ZeptoRowEdit[],
  correctedTitleArabic: string,
  correctedTitleRussian: string,
): ProofreadSuggestionResult[] {
  const correctedById = new Map(
    collectAllProofreadItems(correctedRows, correctedTitleArabic, correctedTitleRussian).map(
      (item) => [item.id, item.text],
    ),
  );
  const out: ProofreadSuggestionResult[] = [];
  for (const item of originalItems) {
    const suggested = correctedById.get(item.id);
    if (
      suggested === undefined ||
      proofreadTextUnchanged(item.text, suggested) ||
      !isAllowedProofreadSuggestion(item, suggested, correctedRows, correctedById)
    ) {
      continue;
    }
    out.push({ id: item.id, suggested });
  }
  return out;
}

async function proofreadLessonZepto(
  lesson: ProofreadLessonZeptoPayload,
  sourceRows: ZeptoRowEdit[],
  originalItems: ProofreadItem[],
  options?: {
    itemsTotal: number;
    rowId?: number;
    batchIndex?: number;
    batchCount?: number;
    rowsDone?: number;
    rowsTotal?: number;
    onProgress?: (progress: ProofreadProgress) => void;
  },
): Promise<ProofreadSuggestionResult[]> {
  const payload = {
    model: PROOFREAD_MODEL,
    response_format: { type: "json_object" },
    reasoning_effort: "medium",
    messages: [
      { role: "system", content: systemPromptAll() },
      { role: "user", content: JSON.stringify(lesson) },
    ],
  };

  const expectedChars = JSON.stringify(lesson).length;
  const itemsTotal = options?.itemsTotal ?? originalItems.length;
  let receivedChars = 0;
  let streaming = false;
  const reportProgress = (percent: number, streamingNow: boolean) => {
    options?.onProgress?.({
      batchIndex: options?.batchIndex ?? 0,
      batchCount: options?.batchCount ?? 1,
      itemsDone: 0,
      itemsTotal,
      rowsDone: options?.rowsDone ?? 0,
      rowsTotal: options?.rowsTotal ?? sourceRows.length,
      percent,
      streaming: streamingNow,
    });
  };

  reportProgress(0, false);

  for (let attempt = 1; attempt <= PROOFREAD_STRUCTURE_MAX_ATTEMPTS; attempt += 1) {
    receivedChars = 0;
    streaming = false;
    reportProgress(0, false);

    const content = (
      await editorOpenAiChatCompletionsStream(payload, (delta) => {
        streaming = true;
        receivedChars += delta.length;
        reportProgress(proofreadStreamPercent(receivedChars, expectedChars), true);
      })
    ).trim();
    if (!content) {
      return [];
    }

    try {
      const { correctedRows, correctedTitleArabic, correctedTitleRussian } =
        parseProofreadCorrectedRows(content, sourceRows, options?.rowId);

      return diffProofreadItems(
        originalItems,
        correctedRows,
        correctedTitleArabic,
        correctedTitleRussian,
      );
    } catch (error) {
      if (!isProofreadStructureError(error) || attempt === PROOFREAD_STRUCTURE_MAX_ATTEMPTS) {
        throw error;
      }
      notifyProofreadStructureRetry(attempt, PROOFREAD_STRUCTURE_MAX_ATTEMPTS);
    }
  }

  return [];
}

export async function proofreadLessonAllItems(
  source: ProofreadLessonSource,
  options?: {
    rowId?: number;
    onProgress?: (progress: ProofreadProgress) => void;
  },
): Promise<ProofreadSuggestionResult[]> {
  const rowId = options?.rowId;
  const onProgress = options?.onProgress;

  if (rowId != null) {
    const items = collectProofreadItemsForSource(source, rowId);
    if (!items.length) {
      return [];
    }
    const sourceRow = source.rows.find((row) => row.rowId === rowId) ?? null;
    if (!sourceRow) {
      return [];
    }
    const lesson = {
      ...buildProofreadRowZeptoPayload(sourceRow),
      translation_hints: buildProofreadTranslationHints(
        source.rows,
        source.titleArabic,
        source.titleRussian,
        rowId,
      ),
    };
    onProgress?.({
      batchIndex: 0,
      batchCount: 1,
      itemsDone: 0,
      itemsTotal: items.length,
      rowsDone: 0,
      rowsTotal: 1,
      percent: 0,
      streaming: false,
    });
    const results = await proofreadLessonZepto(lesson, [sourceRow], items, {
      itemsTotal: items.length,
      rowId,
      batchIndex: 0,
      batchCount: 1,
      rowsDone: 0,
      rowsTotal: 1,
      onProgress,
    });
    onProgress?.({
      batchIndex: 1,
      batchCount: 1,
      itemsDone: items.length,
      itemsTotal: items.length,
      rowsDone: 1,
      rowsTotal: 1,
      percent: 100,
      streaming: false,
    });
    const allowed = new Set(items.map((item) => item.id));
    return results.filter((row) => allowed.has(row.id));
  }

  return proofreadLessonBatch(source, 0, { onProgress });
}

export async function proofreadLessonBatch(
  source: ProofreadLessonSource,
  batchIndex: number,
  options?: {
    onProgress?: (progress: ProofreadProgress) => void;
  },
): Promise<ProofreadSuggestionResult[]> {
  const onProgress = options?.onProgress;
  const rowsTotal = source.rows.length;
  const batchCount = proofreadLessonBatchCount(source.rows);
  if (batchIndex < 0 || batchIndex >= batchCount) {
    return [];
  }

  const batchRows = proofreadLessonBatchRows(source.rows, batchIndex);
  const includeTitles = batchIndex === 0;
  const batchTitleArabic = includeTitles ? source.titleArabic : "";
  const batchTitleRussian = includeTitles ? source.titleRussian : "";
  const batchItems = collectAllProofreadItems(batchRows, batchTitleArabic, batchTitleRussian);
  const rowsDoneStart = batchIndex * PROOFREAD_ROWS_BATCH_SIZE;

  onProgress?.({
    batchIndex,
    batchCount,
    itemsDone: 0,
    itemsTotal: batchItems.length,
    rowsDone: rowsDoneStart,
    rowsTotal,
    percent: 0,
    streaming: false,
  });

  if (!batchItems.length) {
    onProgress?.({
      batchIndex: batchIndex + 1,
      batchCount,
      itemsDone: 0,
      itemsTotal: 0,
      rowsDone: Math.min(rowsDoneStart + batchRows.length, rowsTotal),
      rowsTotal,
      percent: 100,
      streaming: false,
    });
    return [];
  }

  const lesson = {
    ...buildProofreadLessonZeptoPayload(batchRows, batchTitleArabic, batchTitleRussian),
    translation_hints: translationHintsForBatch(
      source.rows,
      source.titleArabic,
      source.titleRussian,
      batchRows,
      includeTitles,
    ),
  };

  const batchResults = await proofreadLessonZepto(lesson, batchRows, batchItems, {
    itemsTotal: batchItems.length,
    batchIndex,
    batchCount,
    rowsDone: rowsDoneStart,
    rowsTotal,
    onProgress,
  });

  onProgress?.({
    batchIndex: batchIndex + 1,
    batchCount,
    itemsDone: batchItems.length,
    itemsTotal: batchItems.length,
    rowsDone: Math.min(rowsDoneStart + batchRows.length, rowsTotal),
    rowsTotal,
    percent: 100,
    streaming: false,
  });

  return batchResults;
}
